import { useEffect, useState } from 'react'
import { Loader2, Plug, Sparkles } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { CustomSelect } from '~/components/ui/custom-select'
import { ErrorBanner } from '~/components/ui/error-banner'
import {
    getMapping,
    ingestByToken,
    issueIngestToken,
    profileApiSample,
    saveMapping,
} from '~/api/monitoring'
import type {
    ApiKeyProfile,
    MappingProposal,
    MonitoringIndicator,
} from '~/api/types'
import { errMessage } from '~/lib/errors'
import { CopyButton, toAbsoluteUrl } from './ingest-helpers'
import { MappingTable } from './mapping-table'

const API_SOURCE_TYPE = 'api'
const DEFAULT_SAMPLE =
    '{"deviceId":"TH-22","sensor":{"temperature_c":4.8},"recordedAt":"2026-06-25T13:40:00Z"}'

/** API·직접 연동 — 토큰 발급 후 샘플 JSON으로 매핑을 잡아 자동 적재를 설정한다. */
export function ApiIngestPanel({
    dashboardId,
    indicators,
}: {
    dashboardId: string
    indicators: MonitoringIndicator[]
}) {
    const [apiToken, setApiToken] = useState<string | null>(null)
    const [apiUrl, setApiUrl] = useState<string | null>(null)
    const [apiSampleText, setApiSampleText] = useState('')
    const [apiProfiles, setApiProfiles] = useState<ApiKeyProfile[]>([])
    const [apiTsCandidates, setApiTsCandidates] = useState<string[]>([])
    const [apiRecordPathCandidates, setApiRecordPathCandidates] = useState<
        string[]
    >([])
    const [apiRecordsPath, setApiRecordsPath] = useState<string>('')
    const [apiMapping, setApiMapping] = useState<Record<string, string>>({})
    const [apiProposals, setApiProposals] = useState<
        Record<string, MappingProposal>
    >({})
    const [apiMeasuredAtKey, setApiMeasuredAtKey] = useState<string>('')
    const [apiTokenBusy, setApiTokenBusy] = useState(false)
    const [apiProfileBusy, setApiProfileBusy] = useState(false)
    const [apiSaveBusy, setApiSaveBusy] = useState(false)
    const [apiTestBusy, setApiTestBusy] = useState(false)
    const [msg, setMsg] = useState<string | null>(null)
    const [err, setErr] = useState<string | null>(null)

    useEffect(() => {
        // 기존 API 매핑이 있으면 불러와 표시한다.
        getMapping(dashboardId, API_SOURCE_TYPE)
            .then((m) => {
                setApiMapping(m.mapping ?? {})
                if (m.measuredAtKey) setApiMeasuredAtKey(m.measuredAtKey)
                if (m.recordsPath) setApiRecordsPath(m.recordsPath)
            })
            .catch(() => setApiMapping({}))
    }, [dashboardId])

    const onIssueToken = async () => {
        setApiTokenBusy(true)
        setErr(null)
        setMsg(null)
        try {
            const res = await issueIngestToken(dashboardId)
            setApiToken(res.ingestToken)
            setApiUrl(toAbsoluteUrl(res.ingestUrl))
        } catch (e: unknown) {
            setErr(errMessage(e, '토큰 발급에 실패했어요.'))
        } finally {
            setApiTokenBusy(false)
        }
    }

    const onApiProfile = async (recordsPath?: string) => {
        setErr(null)
        setMsg(null)
        let sample: unknown
        try {
            sample = JSON.parse(apiSampleText)
        } catch {
            setErr('샘플 JSON 형식이 올바르지 않아요. 다시 확인해 주세요.')
            return
        }
        const path = recordsPath ?? apiRecordsPath
        setApiProfileBusy(true)
        try {
            const res = await profileApiSample(
                dashboardId,
                sample,
                path || null
            )
            setApiProfiles(res.profiles)
            setApiTsCandidates(res.timestampCandidates ?? [])
            setApiRecordPathCandidates(res.recordPathCandidates ?? [])
            const next: Record<string, string> = { ...apiMapping }
            const props: Record<string, MappingProposal> = {}
            for (const p of res.proposals) {
                props[p.sourceKey] = p
                if (p.fieldId) next[p.sourceKey] = p.fieldId
            }
            setApiMapping(next)
            setApiProposals(props)
            // 측정 시각 키 기본값: 후보 1순위(아직 안 정했으면).
            if (!apiMeasuredAtKey && res.timestampCandidates?.length) {
                setApiMeasuredAtKey(res.timestampCandidates[0])
            }
            setMsg('AI가 매핑을 제안했어요. 확인 후 저장하세요.')
        } catch (e: unknown) {
            setErr(errMessage(e, 'AI 매핑에 실패했어요.'))
        } finally {
            setApiProfileBusy(false)
        }
    }

    // 레코드 배열 경로를 바꾸면 그 경로로 다시 프로파일링해 제안을 갱신한다.
    const onApiRecordsPathChange = (path: string) => {
        setApiRecordsPath(path)
        void onApiProfile(path)
    }

    const setApiColMap = (key: string, fieldId: string) => {
        setApiMapping((m) => {
            const next = { ...m }
            if (fieldId) next[key] = fieldId
            else delete next[key]
            return next
        })
    }

    const onApiSave = async () => {
        setApiSaveBusy(true)
        setErr(null)
        try {
            await saveMapping(dashboardId, {
                sourceType: API_SOURCE_TYPE,
                mapping: apiMapping,
                measuredAtKey: apiMeasuredAtKey || null,
                recordsPath: apiRecordsPath || null,
            })
            setMsg(
                '매핑을 저장했어요. 이제 장비가 보낸 데이터가 자동 적재됩니다.'
            )
        } catch (e: unknown) {
            setErr(errMessage(e, '저장에 실패했어요.'))
        } finally {
            setApiSaveBusy(false)
        }
    }

    // 브라우저에서 한 번에 테스트 전송 — 발급 토큰으로 샘플(없으면 기본)을 실제 적재 API에 보낸다.
    const onApiTest = async () => {
        setErr(null)
        setMsg(null)
        if (!apiToken) {
            setErr('먼저 「API 연동 시작 · 토큰 발급」을 눌러 토큰을 발급하세요.')
            return
        }
        let sample: unknown
        try {
            sample = JSON.parse(apiSampleText.trim() || DEFAULT_SAMPLE)
        } catch {
            setErr('샘플 JSON 형식이 올바르지 않아요. 다시 확인해 주세요.')
            return
        }
        setApiTestBusy(true)
        try {
            const res = await ingestByToken(apiToken, sample)
            setMsg(
                res.ingested > 0
                    ? `테스트 전송 성공 · ${res.ingested}건 적재됐어요. 「데이터베이스」에서 확인하세요.`
                    : '테스트 전송은 성공했지만 적재된 값이 0건이에요. 매핑을 저장하면 값이 지표로 적재됩니다.'
            )
        } catch (e: unknown) {
            setErr(errMessage(e, '테스트 전송에 실패했어요.'))
        } finally {
            setApiTestBusy(false)
        }
    }

    const curlExample = `curl -X POST ${apiUrl} \\\n  -H "X-Ingest-Token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '${apiSampleText.trim() || DEFAULT_SAMPLE}'`

    return (
        <section className='border-border bg-card rounded-md border border-dashed p-5'>
            <div className='mb-4 flex items-start gap-3'>
                <div className='bg-accent text-primary grid size-10 shrink-0 place-items-center rounded-md'>
                    <Plug className='size-5' />
                </div>
                <div>
                    <div className='flex items-center gap-2'>
                        <h3 className='text-foreground text-sm font-semibold'>
                            API · 직접 연동
                        </h3>
                        <Badge tone='info'>실시간</Badge>
                    </div>
                    <p className='text-muted-foreground mt-0.5 text-xs'>
                        자동 계측 설비·외부 시스템이 토큰으로 임의 구조 JSON을
                        보내면 적재됩니다. 먼저 샘플로 매핑을 잡아 두세요.
                    </p>
                </div>
            </div>

            {/* 1) 적재 토큰 */}
            <div className='border-border rounded-md border p-4'>
                <p className='text-foreground text-sm font-medium'>
                    1. 적재 토큰
                </p>
                {apiToken && apiUrl ? (
                    <div className='mt-3 grid gap-3'>
                        <div>
                            <p className='text-muted-foreground text-xs'>
                                적재 URL (POST)
                            </p>
                            <div className='mt-1 flex items-center gap-2'>
                                <code className='bg-muted text-foreground min-w-0 flex-1 truncate rounded px-2 py-1.5 text-xs'>
                                    {apiUrl}
                                </code>
                                <CopyButton text={apiUrl} />
                            </div>
                        </div>
                        <div>
                            <p className='text-muted-foreground text-xs'>
                                토큰 (헤더{' '}
                                <code className='text-foreground'>
                                    X-Ingest-Token
                                </code>
                                )
                            </p>
                            <div className='mt-1 flex items-center gap-2'>
                                <code className='bg-muted text-foreground min-w-0 flex-1 truncate rounded px-2 py-1.5 font-mono text-xs'>
                                    {apiToken}
                                </code>
                                <CopyButton text={apiToken} />
                            </div>
                        </div>
                        <div>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={onIssueToken}
                                disabled={apiTokenBusy}>
                                {apiTokenBusy ? (
                                    <Loader2 className='animate-spin' />
                                ) : null}
                                토큰 재발급
                            </Button>
                            <span className='text-muted-foreground ml-2 text-xs'>
                                재발급하면 기존 토큰은 즉시 무효화됩니다.
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className='mt-2 flex items-center gap-3'>
                        <Button onClick={onIssueToken} disabled={apiTokenBusy}>
                            {apiTokenBusy ? (
                                <Loader2 className='animate-spin' />
                            ) : (
                                <Plug />
                            )}
                            API 연동 시작 · 토큰 발급
                        </Button>
                        <span className='text-muted-foreground text-xs'>
                            장비/외부 시스템이 이 토큰으로 데이터를 보냅니다.
                        </span>
                    </div>
                )}
            </div>

            {/* 2) 샘플로 매핑 잡기 */}
            <div className='border-border mt-3 rounded-md border p-4'>
                <div className='flex items-center justify-between gap-3'>
                    <p className='text-foreground text-sm font-medium'>
                        2. 샘플로 매핑 잡기
                    </p>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => void onApiProfile()}
                        disabled={apiProfileBusy || !apiSampleText.trim()}>
                        {apiProfileBusy ? (
                            <Loader2 className='animate-spin' />
                        ) : (
                            <Sparkles />
                        )}
                        AI 매핑 제안
                    </Button>
                </div>
                <textarea
                    value={apiSampleText}
                    onChange={(e) => setApiSampleText(e.target.value)}
                    rows={5}
                    placeholder={
                        '장비가 보낼 샘플 JSON을 붙여넣으세요\n예) {"deviceId":"TH-22","sensor":{"temperature_c":4.8},"recordedAt":"2026-06-25T13:40:00Z"}'
                    }
                    className='border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:ring-ring mt-2 w-full rounded-md border p-3 font-mono text-xs outline-none focus-visible:ring-2'
                />

                {apiProfiles.length > 0 ? (
                    <div className='mt-3'>
                        {apiRecordPathCandidates.length > 0 ? (
                            <div className='mb-3'>
                                <label className='text-muted-foreground text-xs'>
                                    레코드 배열 경로
                                </label>
                                <CustomSelect
                                    ariaLabel='레코드 배열 경로'
                                    value={apiRecordsPath}
                                    onValueChange={onApiRecordsPathChange}
                                    containerClassName='ml-2 inline-block w-auto align-middle'
                                    className='border-border h-8'
                                    options={[
                                        { value: '', label: '(단일 객체)' },
                                        ...apiRecordPathCandidates.map((p) => ({
                                            value: p,
                                            label: p,
                                        })),
                                    ]}
                                />
                                <p className='text-muted-foreground mt-1 text-xs'>
                                    이 경로의 배열을 펼쳐 각 레코드를
                                    적재합니다. 아래 매핑은 펼친 키 기준이에요.
                                </p>
                            </div>
                        ) : null}

                        <MappingTable
                            keys={apiProfiles.map((prof) => ({
                                key: prof.key,
                                numeric: prof.isNumeric,
                                sampleText: prof.sampleValues
                                    .slice(0, 5)
                                    .map((v) => String(v))
                                    .join(', '),
                            }))}
                            mapping={apiMapping}
                            proposals={apiProposals}
                            onMap={setApiColMap}
                            indicators={indicators}
                            keyHeader='소스 키'
                            measuredAtKey={apiMeasuredAtKey}
                            onMeasuredAtKey={setApiMeasuredAtKey}
                            measuredAtLabel='측정 시각 키'
                            measuredAtSearchable
                            measuredAtOptions={[
                                {
                                    value: '',
                                    label: '(없음 — 적재 시각 사용)',
                                },
                                ...apiTsCandidates.map((k) => ({
                                    value: k,
                                    label: `${k} (추천)`,
                                })),
                                ...apiProfiles
                                    .filter(
                                        (p) => !apiTsCandidates.includes(p.key)
                                    )
                                    .map((p) => ({
                                        value: p.key,
                                        label: p.key,
                                    })),
                            ]}
                        />
                    </div>
                ) : null}
            </div>

            {/* 3) 저장 */}
            <div className='border-border mt-3 rounded-md border p-4'>
                <p className='text-foreground text-sm font-medium'>
                    3. 매핑 저장
                </p>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                    저장하면 같은 구조의 데이터가 들어올 때 자동으로 적재됩니다.
                </p>
                <Button
                    className='mt-2'
                    onClick={onApiSave}
                    loading={apiSaveBusy}
                    disabled={Object.keys(apiMapping).length === 0}>
                    매핑 저장
                </Button>
            </div>

            {/* 4) 테스트 전송 — 토큰 발급 후 바로 동작 확인(브라우저 원클릭 또는 curl) */}
            {apiToken && apiUrl ? (
                <div className='border-border mt-3 rounded-md border p-4'>
                    <p className='text-foreground text-sm font-medium'>
                        4. 테스트 전송
                    </p>
                    <p className='text-muted-foreground mt-0.5 text-xs'>
                        발급 토큰으로 샘플 한 건을 실제 적재 API에 보내 동작을
                        확인해요. 매핑을 저장했다면 값이 지표로 적재됩니다.
                    </p>
                    <div className='mt-2 flex items-center gap-2'>
                        <Button
                            onClick={onApiTest}
                            loading={apiTestBusy}
                            disabled={!apiToken}>
                            <Plug className='size-4' />
                            테스트 전송
                        </Button>
                        <span className='text-muted-foreground text-xs'>
                            현재 샘플(없으면 기본 예시)을 한 건 보냅니다.
                        </span>
                    </div>
                    <p className='text-muted-foreground mt-3 text-xs'>
                        또는 터미널에서 아래 명령으로 보낼 수도 있어요.
                    </p>
                    <div className='mt-1 flex items-start gap-2'>
                        <pre className='bg-muted text-foreground min-w-0 flex-1 overflow-x-auto rounded p-3 font-mono text-xs'>
                            {curlExample}
                        </pre>
                        <CopyButton text={curlExample} />
                    </div>
                </div>
            ) : null}

            {msg ? (
                <ErrorBanner
                    tone='success'
                    className='bg-accent text-primary mt-3'>
                    {msg}
                </ErrorBanner>
            ) : null}
            {err ? (
                <ErrorBanner className='bg-point-subtle text-point mt-3'>
                    {err}
                </ErrorBanner>
            ) : null}
        </section>
    )
}
